import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar, User, Trophy } from 'lucide-react';
import Seo from '../components/Seo';
import { fetchPost, fetchPosts } from '../api';

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }); }
  catch (e) { return ''; }
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ok | notfound

  useEffect(() => {
    let active = true;
    (async () => {
      setStatus('loading');
      setRelated([]);
      try {
        const data = await fetchPost(slug);
        if (!active) return;
        setPost(data); setStatus('ok');
        try {
          const all = await fetchPosts();
          const others = (all || []).filter((p) => p.slug !== slug);
          const sameCat = others.filter((p) => p.category === data.category);
          const merged = [...sameCat, ...others.filter((p) => p.category !== data.category)];
          if (active) setRelated(merged.slice(0, 3));
        } catch (e) { /* ignore */ }
      } catch (e) {
        if (active) setStatus('notfound');
      }
    })();
    return () => { active = false; };
  }, [slug]);

  if (status === 'loading') {
    return <div className="flex min-h-[60vh] items-center justify-center bg-[#15120f] text-neutral-400">Loading article…</div>;
  }

  if (status === 'notfound') {
    return (
      <div data-testid="blog-post-notfound" className="flex min-h-[60vh] flex-col items-center justify-center bg-[#15120f] px-4 text-center">
        <Seo title="Article Not Found – 24BetBox" description="The article you are looking for could not be found." path={`/blog/${slug}`} />
        <h1 className="font-heading text-3xl font-extrabold text-white">Article not found</h1>
        <p className="mt-2 text-neutral-400">This post may have been moved or removed.</p>
        <Link to="/blog" className="btn-orange mt-6 inline-flex items-center gap-2 rounded-lg px-6 py-3 font-heading text-sm font-bold uppercase"><ArrowLeft className="h-4 w-4" /> Back to Blog</Link>
      </div>
    );
  }

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.excerpt || post.metaDescription || '',
      image: post.coverImage || 'https://24betbox.com/og-image.jpg',
      datePublished: post.created_at,
      dateModified: post.updated_at || post.created_at,
      author: { '@type': 'Organization', name: post.author || '24BetBox Team' },
      publisher: { '@type': 'Organization', name: '24BetBox', logo: { '@type': 'ImageObject', url: 'https://24betbox.com/og-image.jpg' } },
      mainEntityOfPage: `https://24betbox.com/blog/${post.slug}`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://24betbox.com/' },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://24betbox.com/blog' },
        { '@type': 'ListItem', position: 3, name: post.title, item: `https://24betbox.com/blog/${post.slug}` },
      ],
    },
  ];

  return (
    <div data-testid="blog-post-page">
      <Seo
        title={post.metaTitle || `${post.title} – 24BetBox`}
        description={post.metaDescription || post.excerpt || post.title}
        path={`/blog/${post.slug}`}
        image={post.coverImage || undefined}
        type="article"
        jsonLd={jsonLd}
      />

      <article className="bg-[#15120f]">
        <div className="hero-gradient border-b border-[#2e2822]">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12">
            <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange-400 hover:text-orange-300"><ArrowLeft className="h-4 w-4" /> Back to Blog</Link>
            {post.category && <span className="ml-3 rounded-full bg-orange-500 px-3 py-1 text-[11px] font-extrabold uppercase text-[#15120f]">{post.category}</span>}
            <h1 className="mt-5 font-heading text-3xl font-extrabold leading-tight text-white sm:text-4xl lg:text-5xl">{post.title}</h1>
            <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-neutral-400">
              <span className="inline-flex items-center gap-1.5"><User className="h-4 w-4 text-orange-500" /> {post.author || '24BetBox Team'}</span>
              <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4 text-orange-500" /> {fmtDate(post.created_at)}</span>
            </div>
          </div>
        </div>

        {post.coverImage && (
          <div className="mx-auto max-w-3xl px-4 sm:px-6">
            <img src={post.coverImage} alt={post.title} className="mt-8 h-auto w-full rounded-2xl border border-[#2e2822] object-cover" />
          </div>
        )}

        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12">
          <div className="blog-content" dangerouslySetInnerHTML={{ __html: post.content }} />

          {related.length > 0 && (
            <div className="mt-14" data-testid="related-guides">
              <h2 className="font-heading text-2xl font-extrabold text-white">Related Guides</h2>
              <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((p) => (
                  <Link
                    key={p.id}
                    to={`/blog/${p.slug}`}
                    data-testid="related-guide-card"
                    className="card-dark group flex flex-col overflow-hidden rounded-2xl transition-transform duration-200 hover:-translate-y-1 hover:ring-1 hover:ring-orange-500/40"
                  >
                    <div className="relative h-32 w-full overflow-hidden bg-[#241e18]">
                      {p.coverImage
                        ? <img src={p.coverImage} alt={p.title} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                        : <div className="flex h-full w-full items-center justify-center"><Trophy className="h-8 w-8 text-orange-500/40" /></div>}
                      {p.category && <span className="absolute left-3 top-3 rounded-full bg-orange-500 px-2.5 py-1 text-[10px] font-extrabold uppercase text-[#15120f]">{p.category}</span>}
                    </div>
                    <div className="flex flex-1 flex-col p-4">
                      <h3 className="font-heading text-base font-bold leading-snug text-white group-hover:text-orange-400">{p.title}</h3>
                      <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-orange-400">Read <ArrowRight className="h-3.5 w-3.5" /></span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="mt-12 card-dark rounded-2xl p-8 text-center">
            <Trophy className="mx-auto h-10 w-10 text-orange-500" />
            <h3 className="mt-3 font-heading text-2xl font-extrabold text-white">Put It Into Practice</h3>
            <p className="mx-auto mt-2 max-w-md text-neutral-400">See our free daily football predictions, or unlock premium VIP tips analysed by our expert team.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to="/football-predictions-today" className="inline-flex items-center gap-2 rounded-lg border border-[#3a322a] bg-white/5 px-6 py-3 font-heading text-sm font-bold uppercase tracking-wide text-white hover:border-orange-500/50">Today's Tips</Link>
              <Link to="/pricing" className="btn-orange inline-flex items-center gap-2 rounded-lg px-6 py-3 font-heading text-sm font-bold uppercase tracking-wide">Get VIP Tips</Link>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}
